import dagre from 'dagre';
import type { Edge, Node } from 'reactflow';
import { MarkerType } from 'reactflow';

import { COLOR } from '@/shared/design-tokens/design-tokens';
import type { LineageData, LineageFilterState } from '@/shared/types/lineage';

import { matchesHierarchyFilter } from './filters';

const NODE_WIDTH = 320;
const NODE_HEIGHT = 100;

/**
 * LineageData를 React Flow 노드로 변환
 * 계층 필터에 따라 노드를 그룹화하거나 개별적으로 렌더링하며,
 * Dagre 알고리즘을 사용하여 노드 위치를 자동으로 계산
 */
export const transformToReactFlowNodes = (lineageData: LineageData, filterState: LineageFilterState): Node[] => {
  const nodes: Node[] = [];
  const nodeEntries = Object.entries(lineageData.nodes);
  const selectedHierarchies = filterState.viewOptions.selectedHierarchies;

  const filteredNodes = nodeEntries.filter(([, nodeData]) => {
    const entity = nodeData.entity;
    const isColumnsSelected = selectedHierarchies.has('columns');

    if (!isColumnsSelected && !matchesHierarchyFilter(entity, selectedHierarchies)) {
      return false;
    }

    if (filterState.entityTypes.length > 0 && !filterState.entityTypes.includes(entity.entityType)) {
      return false;
    }

    const depth = Math.abs(nodeData.nodeDepth);
    if (depth > filterState.depth) {
      return false;
    }

    return true;
  });

  const showColumns = filterState.viewOptions.selectedHierarchies.has('columns');

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'LR',
    nodesep: showColumns ? 120 : 80,
    ranksep: 200,
    edgesep: 50,
    marginx: 50,
    marginy: 50,
  });

  const nodeDataMap = new Map<string, [string, LineageData['nodes'][string]]>();
  filteredNodes.forEach(([fqn, nodeData]) => {
    nodeDataMap.set(fqn, [fqn, nodeData]);

    const entity = nodeData.entity;
    const columnCount = showColumns && entity.columns ? entity.columns.length : 0;
    const nodeHeight: number = columnCount > 0 ? Math.max(NODE_HEIGHT, 120 + columnCount * 28) : NODE_HEIGHT;

    dagreGraph.setNode(fqn, { width: NODE_WIDTH, height: nodeHeight });
  });

  const allEdges = {
    ...lineageData.upstreamEdges,
    ...lineageData.downstreamEdges,
  };

  const visibleNodeIds = new Set(filteredNodes.map(([fqn]) => fqn));
  Object.values(allEdges).forEach((edge) => {
    const sourceId = edge.fromEntity.fullyQualifiedName;
    const targetId = edge.toEntity.fullyQualifiedName;

    if (visibleNodeIds.has(sourceId) && visibleNodeIds.has(targetId)) {
      dagreGraph.setEdge(sourceId, targetId);
    }
  });

  dagre.layout(dagreGraph);

  filteredNodes.forEach(([fqn, nodeData]) => {
    const dagreNode = dagreGraph.node(fqn);
    const entity = nodeData.entity;

    const columns =
      showColumns && entity.columns
        ? entity.columns.map((col) => ({
            name: col.name,
            dataType: col.dataType,
            fullyQualifiedName: col.fullyQualifiedName,
          }))
        : undefined;

    const nodeHeight = dagreNode.height;

    const hierarchyPath = [entity.service?.name, entity.database?.name, entity.databaseSchema?.name]
      .filter(Boolean)
      .join(' / ');

    nodes.push({
      id: fqn,
      type: 'lineageNode',
      position: {
        x: dagreNode.x - NODE_WIDTH / 2,
        y: dagreNode.y - nodeHeight / 2,
      },
      data: {
        label: entity.name,
        displayName: entity.displayName,
        entityType: entity.entityType,
        serviceType: entity.serviceType,
        tableType: 'tableType' in entity ? (entity as { tableType?: string }).tableType : undefined,
        fullyQualifiedName: fqn,
        database: entity.database?.name,
        schema: entity.databaseSchema?.name,
        service: entity.service?.name,
        hierarchyPath: hierarchyPath || undefined,
        sourceUrl: 'sourceUrl' in entity ? (entity as { sourceUrl?: string }).sourceUrl : undefined,
        numberOfObjects:
          'numberOfObjects' in entity ? (entity as { numberOfObjects?: number }).numberOfObjects : undefined,
        queries: 'queries' in entity ? (entity as { queries?: unknown[] }).queries : undefined,
        partitions: 'partitions' in entity ? (entity as { partitions?: number }).partitions : undefined,
        replicationFactor:
          'replicationFactor' in entity ? (entity as { replicationFactor?: number }).replicationFactor : undefined,
        retentionTime: 'retentionTime' in entity ? (entity as { retentionTime?: number }).retentionTime : undefined,
        cleanupPolicies:
          'cleanupPolicies' in entity ? (entity as { cleanupPolicies?: string[] }).cleanupPolicies : undefined,
        maximumMessageSize:
          'maximumMessageSize' in entity ? (entity as { maximumMessageSize?: number }).maximumMessageSize : undefined,
        messageSchema:
          'messageSchema' in entity
            ? (entity as { messageSchema?: { schemaType?: string; [key: string]: unknown } }).messageSchema
            : undefined,
        tier: entity.tier,
        owners: entity.owners,
        upstreamCount: nodeData.paging?.entityUpstreamCount || 0,
        downstreamCount: nodeData.paging?.entityDownstreamCount || 0,
        nodeDepth: nodeData.nodeDepth,
        columns,
        showColumns,
        domains: entity.domains,
        tags: entity.tags,
        description: entity.description,
      },
      style: {
        width: NODE_WIDTH,
        height: nodeHeight,
      },
    });
  });

  return nodes;
};

/**
 * LineageData를 React Flow 엣지로 변환
 * 컬럼 레벨 연결이 활성화된 경우 개별 컬럼 간의 연결을 생성하고,
 * 그렇지 않은 경우 테이블 레벨의 연결을 생성
 */
export const transformToReactFlowEdges = (
  lineageData: LineageData,
  visibleNodeIds: Set<string>,
  showColumnLevel: boolean = false
): Edge[] => {
  const edges: Edge[] = [];

  const allEdges = {
    ...lineageData.upstreamEdges,
    ...lineageData.downstreamEdges,
  };

  Object.entries(allEdges).forEach(([edgeId, edgeData]) => {
    const sourceId = edgeData.fromEntity.fullyQualifiedName;
    const targetId = edgeData.toEntity.fullyQualifiedName;

    if (visibleNodeIds.has(sourceId) && visibleNodeIds.has(targetId)) {
      if (showColumnLevel && edgeData.columns && edgeData.columns.length > 0) {
        edgeData.columns.forEach((columnMapping, index) => {
          columnMapping.fromColumns.forEach((fromColumn) => {
            const fromColumnName = fromColumn.split('.').pop() || '';
            const toColumnName = columnMapping.toColumn.split('.').pop() || '';

            edges.push({
              id: `${edgeId}-column-${index}-${fromColumnName}-${toColumnName}`,
              source: sourceId,
              sourceHandle: `${sourceId}-${fromColumnName}-source`,
              target: targetId,
              targetHandle: `${targetId}-${toColumnName}-target`,
              type: 'default',
              animated: false,
              style: {
                stroke: COLOR.GRAY[50],
                strokeWidth: 1.5,
              },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 12,
                height: 12,
                color: COLOR.GRAY[50],
              },
              data: {
                isColumnLevel: true,
                fromColumn: fromColumnName,
                toColumn: toColumnName,
              },
            });
          });
        });
      } else {
        edges.push({
          id: edgeId,
          source: sourceId,
          target: targetId,
          type: 'default',
          animated: false,
          style: {
            stroke: COLOR.GRAY[50],
            strokeWidth: 2,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 12,
            height: 12,
            color: COLOR.GRAY[50],
          },
          data: {
            source: edgeData.source,
            columnMappings: edgeData.columns,
            sqlQuery: edgeData.sqlQuery,
            description: edgeData.description,
          },
        });
      }
    }
  });

  return edges;
};
